import { useEffect, useRef, useState } from 'react';
import { FiCalendar, FiChevronDown } from 'react-icons/fi';
import styles from './SeletorDePeriodo.module.css';
import type { PeriodoExibido } from './fluxoTabela';

interface SeletorDePeriodoProps {
    ano: number;
    /** Nomes dos 12 meses, ja traduzidos. */
    meses: string[];
    /** Indices dos meses com algum lancamento (ganham um ponto no calendario). */
    mesesComMovimento: number[];
    value: PeriodoExibido;
    onChange: (periodo: PeriodoExibido) => void;
    rotuloAnoInteiro: string;
}

/** Periodo exibido do Fluxo de Caixa em forma de calendario: o ano inteiro ou um mes. */
export function SeletorDePeriodo({ ano, meses, mesesComMovimento, value, onChange, rotuloAnoInteiro }: SeletorDePeriodoProps) {
    const [aberto, setAberto] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!aberto) return;
        const fora = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setAberto(false);
        };
        const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAberto(false); };
        document.addEventListener('mousedown', fora);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', fora);
            document.removeEventListener('keydown', esc);
        };
    }, [aberto]);

    const escolher = (periodo: PeriodoExibido) => {
        onChange(periodo);
        setAberto(false);
    };

    const hoje = new Date();
    const mesAtual = hoje.getFullYear() === ano ? hoje.getMonth() : -1;
    const comMovimento = new Set(mesesComMovimento);
    const rotulo = value === 'ano' ? rotuloAnoInteiro : meses[value] ?? '';

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <button
                type="button"
                className={`${styles.trigger}${aberto ? ` ${styles.triggerAberto}` : ''}`}
                onClick={() => setAberto((a) => !a)}
                aria-haspopup="dialog"
                aria-expanded={aberto}
            >
                <FiCalendar size={14} className={styles.triggerIcon} />
                <span className={styles.triggerTexto}>{rotulo}</span>
                <span className={styles.triggerAno}>{ano}</span>
                <FiChevronDown size={14} className={styles.triggerSeta} />
            </button>

            {aberto && (
                <div className={styles.popup} role="dialog">
                    <div className={styles.anoTitulo}>{ano}</div>
                    <button
                        type="button"
                        className={`${styles.anoInteiro}${value === 'ano' ? ` ${styles.selecionado}` : ''}`}
                        aria-pressed={value === 'ano'}
                        onClick={() => escolher('ano')}
                    >
                        {rotuloAnoInteiro}
                    </button>
                    <div className={styles.grade}>
                        {meses.map((nome, i) => (
                            <button
                                key={i}
                                type="button"
                                title={nome}
                                aria-pressed={value === i}
                                className={[
                                    styles.mes,
                                    value === i ? styles.selecionado : '',
                                    i === mesAtual ? styles.mesAtual : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => escolher(i)}
                            >
                                {nome.slice(0, 3)}
                                <span className={`${styles.ponto}${comMovimento.has(i) ? ` ${styles.pontoVisivel}` : ''}`} />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
